import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { TextField } from '@mui/material';
import { state } from '__common/store';

type Props = {
  value: string | number,
  onConfirm?: Function,
  onBlur?: (event: any) => void,
  onCancel?: Function,
  textFieldProps?: Object,
  shouldConfirm: boolean,
  shouldConfirmCb?: (val) => boolean
  title: string,
  content: string,
  applyOnBlur?: boolean,
  disabled?: boolean,
  compactConfirm?: boolean,
};

type State = {
  value: string,
  tempValue: string,
  confirmVisible: boolean,
};

const TYPING_INPUT_TYPES = new Set([
  'insertText',
  'insertFromPaste',
  'insertFromDrop',
  'insertFromYank',
  'insertCompositionText',
  'deleteContentBackward',
  'deleteContentForward',
  'deleteByCut',
  'deleteContent',
  'deleteWordBackward',
  'deleteWordForward',
  'deleteSoftLineBackward',
  'historyUndo',
  'historyRedo',
]);

export default class ConfirmationTextField extends React.Component<Props, State> {
  skipNextBlurCommit = false;
  pendingTypedInput = false;
  confirmDialogOpen = false;
  stepperPointerDown = false;

  constructor(props) {
    super(props);
    this.state = {
      value: props.value,
      tempValue: '',
      confirmVisible: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.value !== this.props.value && this.state.value !== this.props.value) {
      this.confirmDialogOpen = false;
      this.setState({ value: String(this.props.value), tempValue: this.state.value, confirmVisible: false });
    }
  }

  valuesEqual = (a: string | number, b: string | number) => {
    if (String(a) === String(b)) {
      return true;
    }
    const na = Number(a);
    const nb = Number(b);
    return a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }

  commitValue = (value: string) => {
    const { shouldConfirm, onConfirm, shouldConfirmCb } = this.props;
    if (this.confirmDialogOpen || this.state.confirmVisible) {
      return;
    }
    if (this.valuesEqual(value, this.props.value)) {
      this.setState({ value, confirmVisible: false });
      return;
    }
    const confirmed = shouldConfirm && (!shouldConfirmCb || shouldConfirmCb(value));
    if (confirmed) {
      this.confirmDialogOpen = true;
      this.setState({ value, tempValue: String(this.props.value), confirmVisible: true });
    } else {
      this.setState({ value, tempValue: String(this.props.value), confirmVisible: false });
      if (onConfirm) {
        onConfirm(value);
      }
    }
  }

  isTypedValueChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
    if (this.stepperPointerDown) {
      return false;
    }
    if (this.pendingTypedInput) {
      return true;
    }
    const inputType = (event?.nativeEvent as InputEvent | undefined)?.inputType;
    return Boolean(inputType && TYPING_INPUT_TYPES.has(inputType));
  }

  isEditingKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return false;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      return true;
    }
    if (event.key.length !== 1) {
      return false;
    }
    const inputType = ((this.props.textFieldProps || {}) as { type?: string }).type;
    if (inputType === 'number') {
      return /[0-9.eE+-]/.test(event.key);
    }
    return true;
  }

  handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement;
    if (!this.props.applyOnBlur || target?.type !== 'number') {
      this.stepperPointerDown = false;
      return;
    }
    const rect = target.getBoundingClientRect();
    this.stepperPointerDown = event.clientX >= rect.right - 24;
    if (this.stepperPointerDown) {
      this.pendingTypedInput = false;
    }
  }

  handleFocus = () => {
    this.skipNextBlurCommit = false;
  }

  handleFieldChange = (value: string, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (this.props.applyOnBlur) {
      if (this.isTypedValueChange(event)) {
        this.pendingTypedInput = false;
        this.setState({ value });
        return;
      }
      this.commitValue(value);
      this.stepperPointerDown = false;
      return;
    }
    this.commitValue(value);
  }

  handleBlur = (event: any) => {
    this.pendingTypedInput = false;
    if (this.skipNextBlurCommit) {
      this.skipNextBlurCommit = false;
    } else if (this.props.applyOnBlur && !this.state.confirmVisible && !this.confirmDialogOpen) {
      this.commitValue(this.state.value);
    }
    this.props.onBlur?.(event);
  }

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const textFieldProps = (this.props.textFieldProps || {}) as { onKeyDown?: Function };
    if (this.isEditingKey(event)) {
      this.pendingTypedInput = true;
      this.stepperPointerDown = false;
      this.skipNextBlurCommit = false;
    }
    if (this.props.applyOnBlur && event.key === 'Enter') {
      event.preventDefault();
      this.commitValue(this.state.value);
      this.skipNextBlurCommit = true;
      (event.target as HTMLInputElement).blur();
    }
    textFieldProps.onKeyDown?.(event);
  }

  confirm = () => {
    const { onConfirm } = this.props;
    this.confirmDialogOpen = false;
    this.setState({ tempValue: this.state.value, confirmVisible: false });

    if (onConfirm) {
      onConfirm(this.state.value);
    }
  }

  cancel = () => {
    const { onCancel } = this.props;
    this.confirmDialogOpen = false;
    this.setState({ value: this.state.tempValue, confirmVisible: false });

    if (onCancel) {
      onCancel(this.state.tempValue);
    }
  }

  render() {
    const { textFieldProps, title, content, disabled, compactConfirm = true } = this.props;
    const { value, confirmVisible } = this.state;
    const {projectConfiguration:{projectEnvConfig}} = state();
    const {
      onKeyDown: _onKeyDown,
      onBlur: textFieldOnBlur,
      onFocus: textFieldOnFocus,
      ...restTextFieldProps
    } = (textFieldProps || {}) as {
      onKeyDown?: Function,
      onBlur?: Function,
      onFocus?: Function,
      [k: string]: any,
    };
    return (
      <>
        <TextField
          id=""
          value={value}
          variant="standard"
          {...restTextFieldProps}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFieldChange(event.target.value, event)}
          onMouseDown={this.handleMouseDown}
          onFocus={(event) => {
            this.handleFocus();
            textFieldOnFocus?.(event);
          }}
          onBlur={(event) => {
            this.handleBlur(event);
            textFieldOnBlur?.(event);
          }}
          onKeyDown={this.handleKeyDown}
          disabled={disabled || projectEnvConfig?.is_aurora_project || projectEnvConfig?.is_os_project}
        />
        <Dialog
          id="arrays-lost-modal"
          className={compactConfirm ? 'layout-confirm-dialog' : undefined}
          open={confirmVisible}
          onClose={this.cancel}
          maxWidth={compactConfirm ? false : undefined}
          PaperProps={compactConfirm ? { className: 'layout-confirm-dialog-paper' } : undefined}
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {content}
          </DialogContent>
          <DialogActions>
            <Button 
              className="cancel-button" 
              onClick={this.cancel}
              variant="text"
            >
              NO, CANCEL
            </Button>
            <Button 
              className="submit-button" 
              onClick={this.confirm}
              variant="text"
            >
              YES, CONTINUE
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
